import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import SurveyViewModel from '../../ViewModels/SurveyViewModel';
import TextField from '@mui/material/TextField';
import AdditionalInfo from '../../../Popups/AdditionalInfo';
import { useMemo } from 'react';

const timeColumnSx = { width: '8rem', minWidth: '8rem', whiteSpace: 'nowrap' } as const;
const numericColumnSx = { width: '4.5rem', minWidth: '4.5rem', whiteSpace: 'nowrap' } as const;
const averageColumnSx = { width: '5.5rem', minWidth: '5.5rem', whiteSpace: 'nowrap' } as const;
const accumulationColumnSx = { width: '8rem', minWidth: '8rem', whiteSpace: 'nowrap' } as const;

interface EditableTableCellProps {
    value: string | number | null;
    readOnly: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    type?: React.HTMLInputTypeAttribute;
}

const EditableTableCell: React.FC<EditableTableCellProps> = ({ value, readOnly, onChange, type }) => {
    const displayValue =
        type === 'number'
            ? value === -1 || value === null
                ? ''
                : value
            : value ?? '';

    return (
        <TableCell align="center" sx={numericColumnSx}>
            {readOnly ? (
                displayValue
            ) : (
                <TextField
                    value={displayValue}
                    onChange={onChange}
                    type={type}
                    size={'small'}
                    sx={{
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                        '& input[type=number]': {
                            MozAppearance: 'textfield',
                        },
                        px: -2,
                        mx: -2,
                        '& input': {
                            fontSize: '0.70rem',
                            padding: '1px 2px',
                            textAlign: 'center',
                        },
                        '& .MuiInputBase-root': {
                            height: '28px',
                        },
                    }}
                />
            )}
        </TableCell>
    );
};


interface IProps {
    time: string;
    arrivals: number | null;
    departures: number | null;
    currentAccumulation?: number;
    vehicleOccupancyArrivals: number[] | null;
    vehicleOccupancyDepartures: number[] | null;
    hasBrackets: boolean;
    isVehicleOccupancy: boolean;
    rowIndex: number;
    setValue: (survey: SurveyViewModel) => void;
    readOnly: boolean;
    survey: SurveyViewModel;
    transportType: number | null
    hasAnyOcc: boolean;
}

export default function SurveyTableRow({ time, arrivals, departures, currentAccumulation, hasBrackets, vehicleOccupancyArrivals, vehicleOccupancyDepartures, isVehicleOccupancy, rowIndex, setValue, readOnly, survey, transportType, hasAnyOcc }: IProps) {
    const total = (arrivals ?? 0) + (departures ?? 0);
    const inSurvey = arrivals !== null && departures !== null;
    const showOccupancyCells = isVehicleOccupancy && transportType === 1 && hasAnyOcc;

    const rowSpan = (() => {
        if (showOccupancyCells) {
            return 8;
        }
        if (isVehicleOccupancy)
            return 21
        return 4;
    })();

    const handleArrivalsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedSurvey = { ...survey };
        const num = event.target.value.length === 0 ? null : Number(event.target.value);
        updatedSurvey.arrivals[rowIndex] = num;
        setValue(updatedSurvey);
    };

    const handleDeparturesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedSurvey = { ...survey };
        const num = event.target.value.length === 0 ? null : Number(event.target.value);
        updatedSurvey.departures[rowIndex] = num;
        setValue(updatedSurvey);
    };

    const handleOccChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number,
        type: 'arrival' | 'departure'
    ) => {
        const updatedSurvey = { ...survey };
        const val = event.target.value;
        const num = val === '' ? -1 : parseInt(val, 10);

        const baseLength = transportType === 1 ? 2 : 7;
        const getMovementTotal = (values: number[]) =>
            transportType === 1
                ? values.slice(0, 2).reduce((sum, val) => (val !== -1 ? sum + val : sum), 0)
                : values.slice(0, 7).reduce((sum, val, i) => (val !== -1 ? sum + val * (i + 1) : sum), 0);

        if (type === 'arrival') {
            if (!updatedSurvey.vehicleOccupancyArrivals) {
                updatedSurvey.vehicleOccupancyArrivals = [];
            }

            const currentRowArr =
                vehicleOccupancyArrivals ?? Array(baseLength).fill(-1);

            const occArr = [...currentRowArr];
            occArr[index] = num;

            updatedSurvey.vehicleOccupancyArrivals[rowIndex] = occArr.join(' ');

            const allNegativeOne = occArr.slice(0, baseLength).every((val) => val === -1);
            const movementTotal = getMovementTotal(occArr);

            updatedSurvey.arrivals[rowIndex] = allNegativeOne ? null : movementTotal;
        } else {
            if (!updatedSurvey.vehicleOccupancyDepartures) {
                updatedSurvey.vehicleOccupancyDepartures = [];
            }

            const currentRowArr =
                vehicleOccupancyDepartures ?? Array(baseLength).fill(-1);

            const occDeparture = [...currentRowArr];
            occDeparture[index] = num;

            updatedSurvey.vehicleOccupancyDepartures[rowIndex] = occDeparture.join(' ');

            const allNegativeOne = occDeparture.slice(0, baseLength).every((val) => val === -1);
            const movementTotal = getMovementTotal(occDeparture);

            updatedSurvey.departures[rowIndex] = allNegativeOne ? null : movementTotal;
        }

        setValue(updatedSurvey);
    };



    const avgPerVehicleArrivals = useMemo(() => {
        if (!vehicleOccupancyArrivals) return 0;
        const sumVals = vehicleOccupancyArrivals.reduce((sum, val) => sum + val, 0);
        if (sumVals === 0) return 0;
        return vehicleOccupancyArrivals.reduce((sum, val, idx) => sum + val * (idx + 1), 0) / sumVals;
    }, [vehicleOccupancyArrivals]);

    const avgPerVehicleDepartures = useMemo(() => {
        if (!vehicleOccupancyDepartures) return 0;
        const sumVals = vehicleOccupancyDepartures.reduce((sum, val) => sum + val, 0);
        if (sumVals === 0) return 0;
        return vehicleOccupancyDepartures.reduce((sum, val, idx) => sum + val * (idx + 1), 0) / sumVals;
    }, [vehicleOccupancyDepartures]);

    const totalAvgPerVehicle = useMemo(
        () => (avgPerVehicleArrivals + avgPerVehicleDepartures) / 2,
        [avgPerVehicleArrivals, avgPerVehicleDepartures]
    );

    const formattedAccumulation = useMemo(
        () => (hasBrackets ? `(${currentAccumulation})` : currentAccumulation),
        [hasBrackets, currentAccumulation]
    );

    const hasOgvSplitValues = (values: number[] | null) =>
        !!values && values.some(value => value != null && value !== -1);

    const isEmptyReadOnlyRow = readOnly &&
        arrivals === null &&
        departures === null &&
        (transportType !== 1 || (
            !hasOgvSplitValues(vehicleOccupancyArrivals) &&
            !hasOgvSplitValues(vehicleOccupancyDepartures)
        ));


    if (showOccupancyCells) {
        const occArrivals =
            vehicleOccupancyArrivals ?? (!readOnly ? [-1, -1] : []);
        const occDepartures =
            vehicleOccupancyDepartures ?? (!readOnly ? [-1, -1] : []);

        const inboundOGV1 = occArrivals[0] === -1 ? null : occArrivals[0];
        const inboundOGV2 = occArrivals[1] === -1 ? null : occArrivals[1];
        const outboundOGV1 = occDepartures[0] === -1 ? null : occDepartures[0];
        const outboundOGV2 = occDepartures[1] === -1 ? null : occDepartures[1];

        const inboundTotal = inboundOGV1 != null || inboundOGV2 != null
            ? (inboundOGV1 ?? 0) + (inboundOGV2 ?? 0)
            : null;

        const outboundTotal = outboundOGV1 != null || outboundOGV2 != null
            ? (outboundOGV1 ?? 0) + (outboundOGV2 ?? 0)
            : null;

        const totals2 =
            inboundTotal != null && outboundTotal != null
                ? inboundTotal + outboundTotal
                : null;

        const noData =
            occArrivals.every((v) => v === -1) &&
            occDepartures.every((v) => v === -1);

        return (
            <TableRow>
                {noData && readOnly ? (
                    <>
                        <TableCell align="center" sx={timeColumnSx}>{time}</TableCell>
                        <TableCell colSpan={8} align="center">
                            Survey Did Not Take Place During This Time Period
                        </TableCell>
                    </>
                ) : (
                    <>
                        <TableCell align="center" sx={timeColumnSx}>{time}</TableCell>

                        {occArrivals.map((value, index) => (
                            <EditableTableCell
                                value={value}
                                key={`Occarr-${index}`}
                                readOnly={readOnly}
                                type={'number'}
                                onChange={(e) => handleOccChange(e, index, 'arrival')}
                            />
                        ))}
                        <TableCell align="center" sx={numericColumnSx}>{inboundTotal ?? ''}</TableCell>

                        {occDepartures.map((value, index) => (
                            <EditableTableCell
                                value={value}
                                key={`Occdepp-${index}`}
                                readOnly={readOnly}
                                type={'number'}
                                onChange={(e) => handleOccChange(e, index, 'departure')}
                            />
                        ))}
                        <TableCell align="center" sx={numericColumnSx}>{outboundTotal ?? ''}</TableCell>
                        <TableCell align="center" sx={numericColumnSx}>{totals2 ?? ''}</TableCell>
                        <TableCell align="center" sx={accumulationColumnSx}>{formattedAccumulation}</TableCell>
                    </>
                )}
            </TableRow>
        );
    }



    return (
        <TableRow>
            <TableCell align="center" sx={timeColumnSx}>
                {time}
                {rowIndex == -1 && (
                    <AdditionalInfo
                        type="dialog"
                        icon="triangle"
                        colour="warning"
                        dialogTitle="Note on Vehicle Occupants Count Total"
                        dialogContent="Users should note that the total figures displayed in this row represent the number of recorded vehicles with 1 occupant, 2 occupants, 3 occupants, etc. They do not represent the total number of vehicle occupants, as these are shown in the 'Arr', 'Dep' and 'Totals' columns. It should also be noted that some vehicles that are included in the Total Vehicles count may not be included in the Vehicle Occupants count, as drivers of vehicles picking up and dropping off passengers at a site are excluded from the Vehicle Occupants count whenever this is achievable in TRICS surveys. As a result of this it is possible to have a vehicle with zero occupants observed (for example a taxi with a driver arriving to pick someone up), in which case such a vehicle movement would not be present in the Vehicle Occupants table."
                    />
                )}
            </TableCell>
            {(((isVehicleOccupancy && transportType !== 1 && vehicleOccupancyArrivals == null) && !readOnly) || isEmptyReadOnlyRow)
                ?
                <TableCell colSpan={rowSpan} align="center">Survey Did Not Take Place During This Time Period</TableCell>
                :
                <>
                    {isVehicleOccupancy && transportType != 1 ? (
                        <>
                            {vehicleOccupancyArrivals?.map((value, index) => (
                                <EditableTableCell
                                    value={value}
                                    key={`Occarr-${index}`}
                                    readOnly={readOnly}
                                    type={'number'}
                                    onChange={(e) => handleOccChange(e, index, 'arrival')}
                                />
                            ))}
                            <TableCell sx={numericColumnSx}>{arrivals} </TableCell>
                            <TableCell align="center" sx={averageColumnSx}>
                                {inSurvey ? (avgPerVehicleArrivals && avgPerVehicleArrivals != 0 ? avgPerVehicleArrivals.toFixed(1) : 0.0) : ''}
                            </TableCell>

                            {vehicleOccupancyDepartures?.map((value, index) => (
                                <EditableTableCell
                                    value={value}
                                    key={`Occdepp-${index}`}
                                    readOnly={readOnly}
                                    type={'number'}
                                    onChange={(e) => handleOccChange(e, index, 'departure')}
                                />
                            ))}
                            <TableCell sx={numericColumnSx}>{departures}</TableCell>
                            <TableCell align="center" sx={averageColumnSx}>
                                {inSurvey ? (avgPerVehicleDepartures && avgPerVehicleDepartures != 0 ? avgPerVehicleDepartures.toFixed(1) : 0.0) : ''}
                            </TableCell>

                            <TableCell align="center" sx={numericColumnSx}>{inSurvey ? total : undefined}</TableCell>
                            <TableCell align="center" sx={averageColumnSx}>
                                {inSurvey ? (totalAvgPerVehicle && totalAvgPerVehicle != 0 ? totalAvgPerVehicle.toFixed(1) : 0.0) : ''}
                            </TableCell>
                            {rowIndex != -1 && <TableCell align="center" sx={accumulationColumnSx}>{inSurvey ? formattedAccumulation : undefined}</TableCell>}
                            {rowIndex == -1 && <TableCell sx={accumulationColumnSx}></TableCell>}

                        </>
                    ) : (
                        <>
                            <EditableTableCell type={'number'} value={arrivals} readOnly={readOnly} onChange={handleArrivalsChange} />
                            <EditableTableCell type={'number'} value={departures} readOnly={readOnly} onChange={handleDeparturesChange} />
                            <TableCell align="center" sx={numericColumnSx}>{inSurvey ? total : undefined} </TableCell>
                            {rowIndex != -1 && <TableCell align="center" sx={accumulationColumnSx}>{inSurvey ? formattedAccumulation : undefined}</TableCell>}
                            {rowIndex == -1 && <TableCell sx={accumulationColumnSx}></TableCell>}
                        </>
                    )}
                </>
            }
        </TableRow>
    );
}
